import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// antd
import { Form, Input, Typography } from 'antd'

// slices
import { passwordForgot } from './login.slice'

// routes
import { PUBLIC_ROUTES } from '../../routes'

// assets
import logo from '../../assets/images/logo.png'

// components
import Button from '../../components/button/button'
import { FORM_VALIDATION_RULES } from '../../constants/validationRules/form'

// css
import './LoginForm.css'
import { showActionNotification } from '../../components/notification'

const RESET_COOLDOWN_SECONDS = 30

function ForgotPassword() {
    const [form] = Form.useForm()

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { Text } = Typography
    const cooldownTimerRef = useRef(null)

    const [loadingButton, setLoadingButton] = useState(false)
    const [cooldownSeconds, setCooldownSeconds] = useState(0)

    useEffect(() => {
        return () => {
            if (cooldownTimerRef.current) {
                clearInterval(cooldownTimerRef.current)
            }
        }
    }, [])

    const startCooldown = () => {
        if (cooldownTimerRef.current) {
            clearInterval(cooldownTimerRef.current)
        }

        setCooldownSeconds(RESET_COOLDOWN_SECONDS)
        cooldownTimerRef.current = setInterval(() => {
            setCooldownSeconds((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownTimerRef.current)
                    cooldownTimerRef.current = null
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }

    async function onSendOTP(values) {
        if (loadingButton || cooldownSeconds > 0) return

        const email = values?.email?.trim()

        if (!email) {
            showActionNotification({
                type: 'error',
                message: 'Please enter your email address',
            })
            return
        }

        setLoadingButton(true)

        const result = await dispatch(passwordForgot({ email }))
        const data = result?.payload?.data

        setLoadingButton(false)
        startCooldown()

        if (!data) {
            return showActionNotification({
                type: 'error',
                message: 'Something went wrong',
            })
        }

        if (data.success) {
            form.resetFields()
            showActionNotification({
                type: 'success',
                message: data.message,
            })
        } else {
            showActionNotification({
                type: 'error',
                message: data.message || 'Something went wrong',
            })
        }
    }

    const isSubmitDisabled = loadingButton || cooldownSeconds > 0
    const buttonText =
        cooldownSeconds > 0
            ? `Resend in ${cooldownSeconds}s`
            : 'Reset Password'

    return (
        <div className="glass-card">
            <div className="login-logo-container">
                <img src={logo} alt="logo" className="login-form-logo-img" />
            </div>

            <div
                style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    marginBottom: 24,
                }}
            >
                <Text
                    style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 14,
                    }}
                    onClick={() => navigate(PUBLIC_ROUTES.login)}
                >
                    <Icon icon="mdi:arrow-left" width={20} height={20} />
                    Back to Login
                </Text>
            </div>

            <h2 className="welcome-title">Forgot your password?</h2>
            <p className="welcome-subtitle">
                Enter your email and we&apos;ll send you reset password
                instructions.
            </p>

            <Form
                layout="vertical"
                form={form}
                onFinish={onSendOTP}
                requiredMark={false}
                autoComplete="off"
                className="custom-login-form"
                style={{
                    rowGap: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Form.Item
                    name="email"
                    rules={FORM_VALIDATION_RULES.email.rules}
                >
                    <Input
                        className="custom-input"
                        placeholder="Me@example.com"
                        autoComplete="off"
                    />
                </Form.Item>

                <Button
                    props={{
                        text: buttonText,
                        htmlType: 'submit',
                        className: 'login-submit-btn',
                        disabled: isSubmitDisabled,
                        loadingButton: loadingButton,
                    }}
                />
            </Form>
        </div>
    )
}

export default ForgotPassword
